<template>
    <div class="card card-default work-order-card" v-on="isCollapsed ? { click: expand } : {}" :class="{ 'work-order-expanded': isExpanded, 'work-order-collapsed' : !isExpanded }">

        <!-- Image modal -->
        <div class="modal fade" :id="imageModalId" tabindex="-1" role="dialog" aria-labelledby="imageModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="imageModalLabel">{{ enlargeImage.filename }} </h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <img :src="enlargeImage.url" class="w-100">
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="card-header">
            <div class="d-inline-block card-header-content">
                <div class="title">
                    <span v-if="isExpanded && !standalone" class="h6 clickable minus-button" @click="collapse">[–]</span>
                    {{ changeorder.title }}
                </div>
                <div v-if="!isTemplate && !isDraft && isSigned && !savingStatus && (isApproved || isRejected) && changeorder.signer" class="date">
                    {{ status }} by {{ changeorder.signer.name }} on {{ changeOrderDate(changeorder.signed_at) }}
                </div>
                <div v-if="!isTemplate && !isDraft">
                    <span v-if="canSeeCost" class="fw-600" :class="isApproved ? 'text-success' : 'text-muted'">${{ prettyTotal }}</span>
                    <span v-if="isApproved && canSeeCost">•</span>
                    <span v-if="isApproved">
                        <span class="fw-600" :class="isOrdered ? 'text-success' : 'text-muted'">{{ orderedStatus }}</span> • <span class="fw-600" :class="isInvoiced ? 'text-success' : 'text-muted'">{{ invoicedStatus }}</span>
                    </span>
                </div>
                <div v-if="canUpdateChangeOrder && isExpanded && !standalone" class="mt-3">
                    <div class="link d-inline-block" @click="toggleEdit" >{{ currentlyEditing ? 'Cancel' : 'Edit' }} </div>
                    <div v-if="isDeleting" class="d-inline-block ml-2">
                        <div><span class="loader loader-xs mr-2"></span>Deleting...</div>
                    </div>
                    <div v-else-if="!showingDeleteConfirmation" class="d-inline-block">
                        <div class="link d-inline-block ml-2" @click="showDeleteConfirmation">Delete</div>

                        <div v-if="!isDraft" class="d-inline-block">
                            <div v-if="workOrder" class="link d-inline-block ml-2" @click="clickedViewWorkOrder">
                                <i class="fas fa-check-circle text-success"></i> View work order
                            </div>
                            <span v-else-if="isCreatingWorkOrder" class="loader loader-xs ml-2"></span>
                            <div v-else-if="!currentlyEditing" class="link d-inline-block ml-2" @click="clickedCreateWorkOrder">
                                Create work order
                            </div>
                        </div>
                    </div>

                    <div v-else class="d-inline-block ml-2">
                        <span class="font-weight-600">Are you sure?</span>
                        <span class="clickable text-danger ml-2" @click="deleteChangeOrder">Yes</span>
                        <span class="link ml-2" @click="showingDeleteConfirmation = false">Cancel</span>
                    </div>
                    <div v-if="deleteStatusText" class='text-danger'>
                        {{ deleteStatusText }}
                    </div>

                    <div v-if="showNotificationOption" class="text-muted">
                        Sending team notifications via <span :class="pendingNotificationOption ? '' : 'link'" @click="updateNotificationClicked">{{ notificationOption }}</span>
                        <span v-if="!changeorder.use_project_notification_settings" class="clickable remove-slack-button" @click="resetNotifications">Reset</span>
                    </div>

                </div>
            </div>
            <div v-if="isDraft" class="float-right">
                <button class="btn btn-light" :disabled="savingStatus || savingNotifyFlag" @click.stop="release">{{ savingStatus ? 'Releasing...' : 'Release to customer' }}</button>
            </div>
            <div v-if="isCustomer && !isDraft" class="float-right">
                <span v-if="isApproved" class="text-success">
                    <i class="fas fa-check-circle"></i> Approved
                </span>
                <span v-else-if="isRejected" class="text-danger">
                    <i class="fas fa-ban"></i> Declined
                </span>
                <div v-else>
                    <button class="btn btn-sm btn-success mr-2" :disabled="savingStatus" @click="approve">Approve</button>
                    <button class="btn btn-sm btn-light" :disabled="savingStatus" @click="reject">Decline</button>
                </div>
            </div>
            <div v-else-if="!isTemplate && !isDraft && isExpanded && canUpdateChangeOrder" class="float-right card-header-right-content">

                <div class="row">
                    <div class="col-md-12">
                        <!-- NOTIFICATION BELL / BUTTON -->
                        <div v-show="changeorder.notify_team && !currentlyEditing" @click="enableOrDisableNotifications" class="clickable notification-icon float-right" :class="savingNotificationSettings ? 'disabled' : ''" v-tooltip.bottom="'Team notifications for this material selection are ON'">
                            <i class="fas fa-bell mt-1 ml-3"></i>
                        </div>
                        <div v-show="!changeorder.notify_team && !currentlyEditing" @click="enableOrDisableNotifications" class="clickable notification-icon do-not-notify float-right" :class="savingNotificationSettings ? 'disabled' : ''" v-tooltip.bottom="'Team notifications for this material selection are OFF'">
                            <i class="fas fa-bell-slash mt-1 ml-3"></i>
                        </div>

                        <!-- PRIMARY STATUS BUTTON DROPDOWN -->
                        <div class="btn-group float-right">
                            <button type="button" class="btn dropdown-toggle" :class="statusButtonClass" :disabled="savingStatus" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                <span v-if="savingStatus" class="loader loader-sm float-left mr-2"></span>
                                <span v-else>
                                    <i :class="statusButtonIcon"></i>
                                </span>
                                <span class="ml-2">{{ status }}</span>
                            </button>
                            <div class="dropdown-menu dropdown-menu-right">
                                <div class="dropdown-item" :class="status == 'Pending approval' ? '' : 'clickable'" @click="setStatus('Pending approval')"><i class="fas fa-tasks"></i> <span class="ml-2">Pending approval</span></div>
                                <div class="dropdown-item" :class="status == 'Approved' ? '' : 'clickable'" @click="setStatus('Approved')"><i class="fas fa-check-circle"></i> <span class="ml-2">Approved</span></div>
                                <div class="dropdown-item" :class="status == 'Rejected' ? '' : 'clickable'" @click="setStatus('Rejected')"><i class="fas fa-ban"></i> <span class="ml-2">Declined</span></div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <span v-else-if="!isTemplate && !isDraft" class="float-right work-order-alert" :class="isApproved ? 'text-success' : isRejected ? 'text-danger' : ''">
                <i :class="statusButtonIcon"></i><span class="ml-2">{{ status }}</span>
            </span>
            <div v-if="error" class="text-danger text-md-right">Updating the change order status failed. Please try again or get in touch!</div>
        </div>

        <div v-if="shouldShowOrderAndInvoiceOptions">
            <div class="col-md-12 bg-light pr-4 pl-4 pb-3 pt-2 ordered-invoiced-status-container">
                <div class="row">
                    <div class="col-md-6">
                        <div class="d-inline-block">
                            <label class="label small fw-600 text-secondary">Order status</label>
                            <div class="btn-group d-block">
                                <button class="btn dropdown-toggle" :class="orderedButtonClass"  type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <span v-if="savingOrderedStatus" class="loader loader-xs float-left mt-1"></span>
                                    <span v-else>
                                        <i :class="orderedButtonIcon"></i>
                                    </span>
                                    <span class="ml-2"> {{ orderedStatus }} </span>
                                </button>
                                <div class="dropdown-menu">
                                    <div class="dropdown-item clickable" @click="orderDropdownClicked(false)">
                                        <i class="fas fa-clock"></i> <span class="ml-2">Not ordered</span>
                                    </div>
                                    <div class="dropdown-item clickable" @click="orderDropdownClicked(true)">
                                        <i class="fas fa-shipping-fast"></i> <span class="ml-2">Ordered</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-inline-block date-picker-1px-adjustment">
                            <label class="label small fw-600 text-secondary">Ordered date</label>
                            <date-picker-wrapper :date="dateFromString(changeorder.ordered_date)" :placeholder="'Ordered date'" @selected-date="setOrderedDate($event)" :not-before="null">
                            </date-picker-wrapper>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <!-- <i class="fas fa-arrow-right ml-1 mr-1 text-secondary"></i> -->
                        <div class="d-inline-block">
                            <label class="label small fw-600 text-secondary">Invoice status</label>
                            <div class="btn-group d-block">
                                <button class="btn dropdown-toggle" :class="invoicedButtonClass" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <span v-if="savingInvoicedStatus" class="loader loader-xs float-left mt-1"></span>
                                    <span v-else>
                                        <i :class="invoicedButtonIcon"></i>
                                    </span>
                                    <span class="ml-2"> {{ invoicedStatus }} </span>
                                </button>
                                <div class="dropdown-menu">
                                    <div class="dropdown-item clickable" @click="invoiceDropdownClicked('0%')">
                                        <i class="fas fa-clock"></i> <span class="ml-2">Not invoiced</span>
                                    </div>
                                    <div class="dropdown-item clickable" @click="invoiceDropdownClicked('100%')">
                                        <i class="fas fa-file-invoice-dollar"></i> <span class="ml-2">Invoiced</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-inline-block date-picker-1px-adjustment">
                            <label class="label small fw-600 text-secondary">Invoiced date</label>
                            <date-picker-wrapper :date="dateFromString(changeorder.invoiced_date)" :placeholder="'Invoiced date'" @selected-date="setInvoicedDate($event)" :not-before="null">
                            </date-picker-wrapper>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isExpanded" class="card-body">
            <selection-form v-if="!isTemplate && currentlyEditing" v-bind:is-editing="true" :project="project" :changeorder="changeorder" :isDraft="isDraft"></selection-form>
            <template-form v-else-if="isTemplate && currentlyEditing" v-bind:is-editing="true" :template="changeorder"></template-form>
            <div v-else>
                <div class="row">
                    <div class="col-md-9">
                        <div v-if="canSeeCost">
                            <div class="row">
                                <div class="col-md-3 text-muted">
                                    Total
                                </div>
                                <div class="col-md-2 fw-600">
                                    ${{ prettyTotal }}
                                </div>
                            </div>

                            <!-- Subtotal -->
                            <div class="row text-muted">
                                <div class="offset-md-3 col-md-2">
                                    ${{ prettySubtotal }}
                                </div>
                                <div class="col-md-2">
                                    Subtotal
                                </div>
                            </div>

                            <!-- Tax -->
                            <div class="row text-muted mb-2 ">
                                <div class="offset-md-3 col-md-2">
                                    ${{ prettyTax }}
                                </div>
                                <div class="col-md-2">
                                    Tax
                                </div>
                            </div>
                        </div>

                        <div v-if="!isTemplate" class="row mb-2">
                            <div class="col-md-3 text-muted">
                                Product links
                            </div>
                            <div class="col-md-7 ellipsis">
                                <div v-if="changeorder.product_links.length > 0">
                                    <div v-for="(productLink, index) in changeorder.product_links" :key="index" class="ellipsis">
                                        <a :href="productLink" target="_blank">{{ productLink }}</a>
                                    </div>
                                </div>
                                <span v-else class="text-disabled">No product links</span>
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col-md-3 text-muted">
                                Description
                            </div>
                            <div class="col-md-9">
                                <span v-if="description" class="multi-line">{{ description }}</span>
                                <span v-else class="text-disabled">No description</span>
                            </div>
                        </div>

                        <div v-if="!isTemplate && !isCustomer" class="row mb-2">
                            <div class="col-md-3 text-muted">
                                Notify customer about changes
                            </div>
                            <div class="col-md-9">
                                <div class="mt-2">
                                    <toggle-button :value="notifyCustomer"
                                                   @change="notifyCustomerChanged"
                                                   color="#000000"
                                                   :sync="true"
                                                   :labels="{checked: 'Yes', unchecked: 'No'}"
                                                   />
                                </div>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-md-3 text-muted">
                                <span v-if="changeorder.images.length == 1">Image</span>
                                <span v-else>Images</span>
                            </div>
                            <div class="col-md-9">
                                <div v-if="changeorder.images.length > 0" class="row">
                                    <div v-for="image in changeorder.images" :key="image.id" class="col-md-6 mb-4">
                                        <img :src="image.url" class="card-image img-fluid img-thumbnail link" @click="clickedEnlargeImage(image)" />
                                    </div>
                                </div>
                                <span v-else class="text-disabled">No images</span>
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col-md-3 text-muted">
                                <span v-if="changeorder.files && changeorder.files.length == 1">File</span>
                                <span v-else>Files</span>
                            </div>
                            <div class="col-md-9">
                                <div v-if="changeorder.files && changeorder.files.length > 0" class="row">
                                    <div v-for="file in changeorder.files" :key="file.id" class="col-md-12">
                                        <a class="d-block" :href="file.url" target="_blank">{{ file.filename }}</a>
                                    </div>
                                </div>
                                <span v-else class="text-disabled">No files</span>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>
        <notes v-if="!isTemplate && isExpanded && !isCustomer" :type="'selection'" :changeorder="changeorder" :user="user" :role="role" :key="changeorder.id"></notes>
    </div>
</template>

<script>
    //import smoothReflow from 'vue-smooth-reflow';
    export default {
        props: ['role', 'user', 'project', 'changeorder', 'standalone'],
        mixins: [require('../change-orders/mixins/orderedInvoicedStatusMixin'), require('../change-orders/mixins/costMixin')],
        //mixins: [smoothReflow],
        created: function() {
            this.getBtnClassForStatus(this.changeorder.status);
            var vm = this;
            Bus.$on('new-selection-canceled', function() {
                vm.currentlyEditing = false;
            });
            Bus.$on('selection-updated', function(changeorder) {
                if (changeorder.id == vm.changeorder.id) {
                    vm.currentlyEditing = false;
                }
            });
            Bus.$on('new-draft-selection-canceled', function() {
                vm.currentlyEditing = false;
            });
            Bus.$on('draft-selection-updated', function(changeorder) {
                if (changeorder.id == vm.changeorder.id) {
                    vm.currentlyEditing = false;
                }
            });
            Bus.$on('new-template-canceled', function() {
                vm.currentlyEditing = false;
            });
            Bus.$on('template-updated', function(changeorder) {
                if (changeorder.id == vm.changeorder.id) {
                    vm.currentlyEditing = false;
                }
            });
        },

        mounted: function() {
            // this.$smoothReflow({
            //     property: ['height'],
            //     transition: 'height .25s ease-in-out, width .75s ease-out'
            // });
            this.isExpanded = this.standalone || this.isCustomer;
        },

        data: function() {
            return {
                currentlyEditing: false,
                isExpanded: this.standalone || this.isCustomer,
                error: false,
                savingStatus: false,
                savingNotifyFlag: false,
                isDeleting: false,
                deleteStatusText: null,
                isCreatingWorkOrder: false,
                createWorkOrderSuccess: false,
                workOrder: null,
                showingDeleteConfirmation: false,
                statusButtonClass: 'btn-light',
                statusAlertClass: 'alert-light work-order-alert',
                statusButtonIcon: 'fas fa-tasks',
                savingStatusText: 'Not started',
                notifyCustomer: this.changeorder ? !!this.changeorder.notify_customer : false,
                enlargeImage: { url: '', filename: '' },
                savingNotificationSettings: false,
                pendingNotificationOption: null,
            };
        },

        watch: {
            changeorder: function (newChangeOrder, oldChangeOrder) {
                if (newChangeOrder != oldChangeOrder) {
                    this.getBtnClassForStatus(newChangeOrder.status);
                    this.notifyCustomer = !!newChangeOrder.notify_customer;
                }
            },

            notifyCustomer: function(newVal, oldVal) {
                this.notifyCustomer = newVal;
            },
        },

        computed: {
            notificationOption: function() {
                if (this.pendingNotificationOption) return this.pendingNotificationOption;
                return this.changeorderNotificationOption;
            },

            changeorderNotificationOption: function() {
                if (this.changeorder.notify_slack && this.changeorder.notify_email) {
                    return 'Slack & email';
                }
                if (this.changeorder.notify_slack) {
                    return 'Slack';
                }
                if (this.changeorder.notify_email) {
                    return 'email';
                }
            },

            showNotificationOption: function() {
                if (this.isTemplate || this.isDraft) return false;

                if (this.canUpdateChangeOrder && !this.currentlyEditing && this.changeorder.notify_team && this.isExpanded && this.project.slack_webhook_channel) {
                    return true;
                }
                return false;
            },

            imageModalId: function() {
                return this.changeorder.id + '-imageModal';
            },

            isTemplate: function() {
                return this.changeorder.is_template;
            },

            isDraft: function() {
                return this.changeorder.is_draft;
            },

            canSeeCost: function() {
                return (this.isCustomer || this.role === 'owner' || this.role === 'administrator') && (this.changeorder.cost > 0 || this.changeorder.cost < 0);
            },

            canUpdateChangeOrder: function() {
                return (this.role === 'owner' || this.role === 'administrator');
            },

            isCustomer: function() {
                return this.role === 'customer';
            },

            isSigned:function() {
                return this.changeorder.signed;
            },

            isApproved: function() {
                return this.changeorder.status === 'Approved';
            },

            isRejected: function() {
                return this.changeorder.status === 'Rejected';
            },

            isCollapsed: function() {
                return !this.isExpanded;
            },

            description: function() {
                // if (!this.changeorder.description) {
                //     return 'none';
                // }

                return this.changeorder.description;
            },

            status: function() {
                if (this.savingStatus) {
                    return this.savingStatusText;
                }

                if (this.isRejected) return "Declined";

                return this.changeorder.status;
            }
        },

        methods: {
            updateNotificationClicked: function(event) {

                if (this.pendingNotificationOption) {
                    return;
                }

                if (this.notificationOption === 'Slack & email') {
                    this.pendingNotificationOption = 'Slack';
                }
                else if (this.notificationOption === 'Slack') {
                    this.pendingNotificationOption = 'email';
                }
                else if (this.notificationOption === 'email') {
                    this.pendingNotificationOption = 'Slack & email';
                }

                this.updateNotification();
            },

            updateNotification: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/changeorders/${this.changeorder.id}/notifications`, {
                            slack: this.pendingNotificationOption.toLowerCase().includes('slack'),
                            email: this.pendingNotificationOption.toLowerCase().includes('email')
                        })
                        .then(response => {
                            Bus.$emit('selection-updated', response.data);
                            this.savingNotificationSettings = false;
                            this.pendingNotificationOption = null;
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                            this.pendingNotificationOption = null;
                        });
            },

            enableOrDisableNotifications: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/changeorders/${this.changeorder.id}/notifications`, {
                            enable: !this.changeorder.notify_team
                        })
                        .then(response => {
                            this.savingNotificationSettings = false;
                            Bus.$emit('selection-updated', response.data);
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                        });
            },

            resetNotifications: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/changeorders/${this.changeorder.id}/notifications`, {
                            reset: true
                        })
                        .then(response => {
                            this.savingNotificationSettings = false;
                            Bus.$emit('selection-updated', response.data);
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                        });
            },

            clickedViewWorkOrder: function() {
                $('a[href="#work-orders"]').trigger('click');
            },

            clickedEnlargeImage: function(image) {
                this.enlargeImage = image;
                $('#' + this.imageModalId).modal('show');
            },

            notifyCustomerChanged: function(event) {
                this.notifyCustomer = event.value;
                this.updateNotifyCustomer();
            },

            showDeleteConfirmation: function() {
                this.showingDeleteConfirmation = true;
            },

            clickedCreateWorkOrder: function() {
                this.createWorkOrderFromChangeOrder();
            },

            toggleEdit: function() {
                this.currentlyEditing = !this.currentlyEditing;
            },

            expand: function() {
                this.isExpanded = true;
            },

            collapse: function() {
                this.isExpanded = false;
            },

            hoverDate: function (date) {
                return moment.utc(date).local().format('MMMM DD YYYY hh:mm A');
            },

            prettyDate: function(date)
            {
                return moment.utc(date).local().fromNow();
            },

            changeOrderDate: function(date)
            {
                return moment(date).format('MMMM D YYYY');
            },

            getBtnClassForStatus: function(status) {
                if (status == 'Pending approval') {
                    this.statusButtonClass = 'btn-light';
                    this.statusAlertClass = 'alert-info work-order-alert';
                    this.statusButtonIcon = 'fas fa-tasks';
                }
                else if (status == 'Approved') {
                    this.statusButtonClass = 'btn-success';
                    this.statusAlertClass = 'alert-success work-order-alert';
                    this.statusButtonIcon = 'fas fa-check-circle';
                }
                else if (status == 'Rejected') {
                    this.statusButtonClass = 'btn-danger';
                    this.statusAlertClass = 'alert-danger work-order-alert';
                    this.statusButtonIcon = 'fas fa-ban';
                }
                else {
                    this.statusButtonClass = 'btn-light';
                    this.statusAlertClass = 'alert-light work-order-alert';
                    this.statusButtonIcon = 'fas fa-tasks';
                }
            },

            approve: function() {
                this.setStatus('Approved');
            },

            reject: function() {
                this.setStatus('Rejected');
            },

            updateNotifyCustomer: function() {
                this.savingNotifyFlag = true;
                axios.post(`/api/changeorders/${this.changeorder.id}/notifyCustomer`, {
                        notifyCustomer: this.notifyCustomer
                    })
                    .then(response => {
                        this.savingNotifyFlag = false;
                    })
                    .catch(error => {
                        this.savingNotifyFlag = false;
                        this.notifyCustomer = this.changeorder.notify_customer;
                    });
            },

            release: function() {
                this.error = false;
                this.savingStatus = true;

                axios.post(`api/selections/drafts/${this.changeorder.id}/release`)
                    .then(response => {
                        this.savingStatus = false;
                        Bus.$emit('draft-selection-deleted', response.data);
                        Bus.$emit('new-selection-created', response.data);
                        /*mixpanel.track('Draft Selection Released', {
                            title: response.data.title,
                        });*/
                    })
                    .catch(error => {
                        this.savingStatus = false;
                        this.error = true;
                    });
            },

            setStatus: function(status) {
                this.error = false;

                if (this.changeorder.status != status) {
                    this.getBtnClassForStatus(status);
                    if (status === 'Rejected') {
                        this.savingStatusText = "Declined";
                    }
                    else {
                        this.savingStatusText = status;
                    }
                    this.savingStatus = true;
                    axios.post(`/api/changeorders/${this.changeorder.id}/status`, {
                            status: status
                        })
                        .then(response => {
                            this.savingStatus = false;
                            if (this.isTemplate) {
                                Bus.$emit('template-updated', response.data);
                            }
                            else {
                                Bus.$emit('selection-updated', response.data);
                            }
                            /*mixpanel.track('Selection Status Changed', {
                                title: response.data.title,
                                status: status
                            });*/
                        })
                        .catch(error => {
                            this.getBtnClassForStatus(this.changeorder.status);
                            this.savingStatus = false;
                            this.error = true;
                        });
                }
            },

            deleteChangeOrder: function() {
                this.isDeleting = true;
                this.deleteStatusText = null;
                axios.delete(`/api/changeorders/${this.changeorder.id}`)
                    .then(response => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;
                        if (this.isTemplate) {
                            Bus.$emit('template-deleted', response.data);
                        }
                        else if (this.isDraft) {
                            Bus.$emit('draft-selection-deleted', response.data);
                            /*mixpanel.track('Draft Selection Deleted', {
                                title: response.data.title
                            });*/
                        }
                        else {
                            Bus.$emit('selection-deleted', response.data);
                            /*mixpanel.track('Selection Deleted', {
                                title: response.data.title
                            });*/
                        }
                    })
                    .catch(error => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;

                        if (error.response.data.error) {
                            this.deleteStatusText = error.response.data.error;
                        }
                        else {
                            this.deleteStatusText = 'We couldn\'t delete this material selection. Please try again or get in touch!';
                        }
                    });
            },

            createWorkOrderFromChangeOrder: function() {
                this.isCreatingWorkOrder = true;
                axios.post(`/api/changeorders/${this.changeorder.id}/createWorkOrder`)
                    .then(response => {
                        this.isCreatingWorkOrder = false;
                        this.createWorkOrderSuccess = true;
                        this.workOrder = response.data;
                        Bus.$emit('new-work-order-created', this.workOrder);
                        /*mixpanel.track('Work Order Created from Selection', {
                            title: response.data.title,
                        });*/
                    })
                    .catch(error => {
                        this.isCreatingWorkOrder = false;
                        this.createWorkOrderSuccess = false;
                    });
            },
        }
    }
</script>
